import { injectable } from 'tsyringe';
import type { PermissionEntry } from '@bmai/auth-api-client';
import { authClient } from '@/shared/AuthApiClient';

export interface RoleView {
    id: string;
    name: string;
    description: string;
    isSystem: boolean;
    permissions: RoleGrant[];
}

export interface RoleGrant {
    resource: string;
    scopes: string[];
}

export interface CreateRoleInput {
    name: string;
    description: string;
}

export interface AssignRolePermissionsInput {
    id: string;
    grants: RoleGrant[];
}

@injectable()
export class RolesAdminService {
    async listRoles(): Promise<RoleView[]> {
        const data = await authClient.rolesApi.getRoles();
        return data.map((r) => ({
            id: r.id,
            name: r.name,
            description: r.description,
            isSystem: r.isSystem,
            permissions: groupByResource(r.permissions),
        }));
    }

    async listPermissions(): Promise<PermissionEntry[]> {
        return authClient.permissionsApi.getPermissions();
    }

    async createRole(input: CreateRoleInput): Promise<void> {
        await authClient.rolesApi.postRoles({
            postRolesRequest: { name: input.name, description: input.description },
        });
    }

    async assignRolePermissions(input: AssignRolePermissionsInput): Promise<void> {
        const permissions: PermissionEntry[] = input.grants.flatMap((g) =>
            g.scopes.map((action) => ({ resource: g.resource, action })),
        );
        await authClient.rolesApi.putByIdPermissions({
            id: input.id,
            putByIdPermissionsRequest: { permissions },
        });
    }

    async deleteRole(id: string): Promise<void> {
        await authClient.rolesApi.deleteById({ id });
    }
}

function groupByResource(entries: PermissionEntry[]): RoleGrant[] {
    const map = new Map<string, Set<string>>();
    for (const entry of entries) {
        const set = map.get(entry.resource) ?? new Set<string>();
        set.add(entry.action);
        map.set(entry.resource, set);
    }
    return [...map.entries()]
        .map(([resource, scopes]) => ({ resource, scopes: [...scopes].sort() }))
        .sort((a, b) => a.resource.localeCompare(b.resource));
}
